import { useState, useCallback } from 'react';
import { doc, writeBatch, collection, getDocs } from 'firebase/firestore';
import { firestore } from '../../firebase';
import { deleteChatMemories } from '../../utils/memoryApi';

export const useModalState = (chats) => {
  const [deleteModal, setDeleteModal] = useState({ visible: false, chatId: null, chatTopic: '' });
  const [progressModal, setProgressModal] = useState({ visible: false, progress: 0, total: 0, operation: '' });
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const showDeleteModal = useCallback((chatId) => {
    if (!chatId) return;
    
    const chat = chats.find(c => c.id === chatId);
    setDeleteModal({
      visible: true,
      chatId,
      chatTopic: chat?.topic || 'Untitled Chat'
    });
  }, [chats]);

  const hideDeleteModal = useCallback(() => {
    setDeleteModal({ visible: false, chatId: null, chatTopic: '' });
  }, []);

  const confirmDeleteChat = useCallback(async () => {
    const { chatId } = deleteModal;
    if (!chatId) return;

    try {
      const batch = writeBatch(firestore);
      const chatRef = doc(firestore, 'chats', chatId);

      const messagesRef = collection(firestore, 'chats', chatId, 'messages');
      const messagesSnapshot = await getDocs(messagesRef);
      messagesSnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });

      batch.delete(chatRef);

      await batch.commit();
      try {
        await deleteChatMemories(chatId);
      } catch (memoryError) {
        console.error('Error deleting chat memories:', memoryError);
      }
      hideDeleteModal();
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  }, [deleteModal, hideDeleteModal]);

  const startRenaming = useCallback((chatId) => {
    if (!chatId) return;
    
    const chat = chats.find(c => c.id === chatId);
    if (!chat) return;
    
    setEditingChatId(chatId);
    setEditingValue(chat.topic || 'Untitled Chat');
  }, [chats]);

  const handleSaveRename = useCallback(async (chatId, renameChat) => {
    if (!chatId || !editingValue.trim()) {
      setEditingChatId(null);
      setEditingValue('');
      return;
    }

    await renameChat(chatId, editingValue);
    setEditingChatId(null);
    setEditingValue('');
  }, [editingValue]);

  const handleCancelRename = useCallback(() => {
    setEditingChatId(null);
    setEditingValue('');
  }, []);

  const handleRenameKeyDown = useCallback((e, chatId, renameChat) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveRename(chatId, renameChat);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelRename();
    }
  }, [handleSaveRename, handleCancelRename]);

  return {
    deleteModal,
    progressModal,
    editingChatId,
    editingValue,
    setProgressModal,
    setEditingValue,
    showDeleteModal,
    hideDeleteModal,
    confirmDeleteChat,
    startRenaming,
    handleSaveRename,
    handleCancelRename,
    handleRenameKeyDown
  };
};
